// src/components/math/MathRenderer.jsx
import React, { useEffect, useRef, useState } from 'react';
import { MathUtils } from './MathUtils';

const MathRenderer = ({
  content,
  className = '',
  enableMarkdown = true,
  enableMath = true,
}) => {
  const containerRef = useRef(null);
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const renderContent = async () => {
      if (!containerRef.current || !content) {
        setIsReady(true);
        return;
      }

      try {
        setError(null);
        setIsReady(false);

        const buildPlainHtml = (text) => {
          if (!text) return text;
          const normalisedText = text.replace(/\r\n/g, '\n');
          const lines = normalisedText.split('\n');
          const parts = [];

          lines.forEach((line, idx) => {
            const trimmed = line.trim();
            const inlineMatch = trimmed.match(/^\$([^$]+)\$$/);
            const parenMatch = trimmed.match(/^\\\(([\s\S]+)\\\)$/);

            if (inlineMatch || parenMatch) {
              const latex = (inlineMatch?.[1] || parenMatch?.[1] || '').trim();
              parts.push(`<div class="math-line">\\(${latex}\\)</div>`);
            } else {
              parts.push(line);
            }

            if (idx < lines.length - 1) {
              parts.push('<br>');
            }
          });

          return parts.join('');
        };

        let finalContent = content;
        const hasMath = MathUtils.containsMathExpressions(content);

        if (enableMarkdown) {
          finalContent = await MathUtils.processContentWithMarkdown(content);
        } else {
          const normalisedContent = hasMath ? buildPlainHtml(content) : content;
          if (hasMath) {
            finalContent = MathUtils.processMathExpressions(normalisedContent);
          }
          if (!hasMath) {
            finalContent = finalContent.replace(/\n/g, '<br>');
          }
        }

        if (enableMath && hasMath) {
          finalContent = MathUtils.convertToExplicitMathJaxDelimiters(finalContent);
        }

        if (containerRef.current) {
          containerRef.current.innerHTML = finalContent;

          if (enableMath && hasMath) {
            await MathUtils.renderMath(containerRef.current);
          }
        }

        setIsReady(true);
      } catch (err) {
        console.error('Error rendering content:', err);
        setError(err.message);

        const fallbackContent = content.replace(/\n/g, '<br>');
        if (containerRef.current) {
          containerRef.current.innerHTML = fallbackContent;
        }
        setIsReady(true);
      }
    };

    renderContent();
  }, [content, enableMarkdown, enableMath]);

  if (error) {
    return (
      <div className={`content-error ${className}`}>
        <div className="text-red-600 text-sm mb-2">
          Content rendering error: {error}
        </div>
        <div
          dangerouslySetInnerHTML={{ __html: content?.replace(/\n/g, '<br>') || '' }}
        />
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={`markdown-content ${className} ${!isReady ? 'content-loading' : ''}`}
      style={{
        lineHeight: '1.6',
        fontSize: '1rem',
        opacity: isReady ? 1 : 0.7
      }}
    />
  );
};

export default MathRenderer;
